// Detect the local player's research project expiring across snapshots and
// surface it as a breakthrough toast (art audit P6).
//
// The signal is the sim's own publication rule: a player global carries
// `research` ONLY while a project is active (`remaining_ticks > 0`), so the
// field vanishing from one snapshot to the next IS the completion — no sim
// change, no new event, and the local player's own state leaks nothing.
// The completion is a *diff*, the one thing a client can know without the
// sim announcing it.

import type { GameSnapshot } from '@aod/protocol';
import { useCallback, useEffect, useRef, useState } from 'react';
import { type SceneRef, researchScene } from '../ui/sceneEvents.js';

/** Toast stays this long (ms), then goes away on its own. */
const TOAST_DURATION_MS = 6000;

export interface ResearchToast {
  readonly id: number;
  readonly scene: SceneRef;
}

export interface UseResearchToastsResult {
  readonly toasts: ReadonlyArray<ResearchToast>;
  readonly dismiss: (id: number) => void;
}

/** Does the local player's global carry an active research project? */
function hasActiveResearch(snapshot: GameSnapshot | null, player: number): boolean | null {
  const globals = snapshot?.state.globals as
    | { players?: ReadonlyArray<{ playerIndex?: number; research?: { active?: boolean } | null }> }
    | undefined;
  const players = globals?.players;
  if (!Array.isArray(players)) return null; // no snapshot yet — resync, don't fire
  const mine = players.find((p) => p?.playerIndex === player);
  if (!mine) return null;
  return mine.research?.active === true;
}

export function useResearchToasts(
  snapshot: GameSnapshot | null,
  localPlayerIndex: number,
): UseResearchToastsResult {
  const [toasts, setToasts] = useState<ReadonlyArray<ResearchToast>>([]);
  const hadResearchRef = useRef<boolean | null>(null);
  const nextIdRef = useRef(1);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    const had = hasActiveResearch(snapshot, localPlayerIndex);
    if (had === null) {
      // No readable state (menu, loading): resync so the first real
      // snapshot can't fire a phantom completion.
      hadResearchRef.current = null;
      return;
    }
    const previous = hadResearchRef.current;
    hadResearchRef.current = had;
    if (previous === null || previous === had) return;
    // The project expired: it was active, and now the sim publishes none.
    if (previous && !had) {
      const toast = { id: nextIdRef.current++, scene: researchScene() };
      setToasts((prev) => [...prev.slice(-2), toast]);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setToasts([]), TOAST_DURATION_MS);
    }
  }, [snapshot, localPlayerIndex]);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  return { toasts, dismiss };
}
